import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import NotificationPanel from './NotificationPanel';
import { supabase } from '../../lib/supabase';
import { getFirstName } from '../../lib/name';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

interface MenuItem {
  label: string;
  icon: string;
  path: string;
  roles: string[];
}

interface ChildInfo {
  full_name: string;
  admission_number: string;
  class_name: string;
  grade_level: string;
  section: string;
}

const menuItems = [
  { label: "Dashboard", icon: "ri-dashboard-line", path: "/dashboard", roles: ["admin", "teacher", "student", "parent"] },
  { label: "Classes", icon: "ri-school-line", path: "/classes", roles: ["admin"] },
  { label: "Subjects", icon: "ri-book-line", path: "/subjects", roles: ["admin"] },
  { label: "Teachers", icon: "ri-user-star-line", path: "/teachers", roles: ["admin"] },
  { label: "Parents", icon: "ri-parent-line", path: "/parents", roles: ["admin"] },
  { label: "Students", icon: "ri-user-line", path: "/students", roles: ["admin"] },
  { label: "Timetable", icon: "ri-calendar-schedule-line", path: "/timetable", roles: ["admin"] },
  { label: "Attendance", icon: "ri-checkbox-circle-line", path: "/attendance", roles: ["admin", "teacher"] },
  { label: "Assessment & Exams", icon: "ri-file-list-3-line", path: "/exams", roles: ["admin"] },
  { label: "Results", icon: "ri-bar-chart-box-line", path: "/results", roles: ["admin", "teacher"] },
  { label: "Report Card", icon: "ri-file-text-line", path: "/report-card", roles: ["admin"] },
  { label: "Transport", icon: "ri-bus-line", path: "/transport", roles: ["admin"] },
  { label: "Transport", icon: "ri-bus-line", path: "/teacher-transport", roles: ["teacher"] },
  { label: "Announcements", icon: "ri-megaphone-line", path: "/announcements", roles: ["admin", "teacher"] },
  { label: "Class Notes", icon: "ri-folder-upload-line", path: "/class-notes", roles: ["admin"] },
  { label: "Lesson Notes", icon: "ri-book-2-line", path: "/admin-lesson-notes", roles: ["admin"] },
  { label: "Lesson Notes", icon: "ri-book-2-line", path: "/lesson-notes", roles: ["teacher"] },
  { label: "Timetable", icon: "ri-calendar-schedule-line", path: "/timetable", roles: ["student"] },
  { label: "My Results", icon: "ri-bar-chart-box-line", path: "/student-results", roles: ["student"] },
  { label: "Class Notes", icon: "ri-folder-open-line", path: "/student-notes", roles: ["student"] },
  { label: "Report Card", icon: "ri-file-text-line", path: "/report-card", roles: ["student"] },
  { label: "Events", icon: "ri-calendar-event-line", path: "/events", roles: ["admin", "teacher", "student", "parent"] },
  { label: "My Timetable", icon: "ri-calendar-schedule-line", path: "/parent-timetable", roles: ["parent"] },
  { label: "Attendance", icon: "ri-checkbox-circle-line", path: "/parent-attendance", roles: ["parent"] },
  { label: "Transport", icon: "ri-bus-line", path: "/parent-transport", roles: ["parent"] },
  // { label: "School Fees", icon: "ri-money-dollar-circle-line", path: "/school-fees", roles: ["admin", "parent", "student"] },
  { label: "Announcements", icon: "ri-megaphone-line", path: "/parent-announcements", roles: ["parent"] },
  { label: "Admissions", icon: "ri-user-add-line", path: "/admission-applications", roles: ["admin"] },
  { label: "Contact Messages", icon: "ri-mail-line", path: "/contact-messages", roles: ["admin"] },
];

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const notificationRef = useRef<HTMLDivElement>(null);
  const { user, signOut, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [childInfo, setChildInfo] = useState<ChildInfo | null>(null);

  // Detect mobile and auto-collapse sidebar
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) setSidebarOpen(false);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (user?.role === 'parent') {
      fetchChildInfo();
    }
  }, [user?.id, user?.role]);

  const fetchChildInfo = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('students')
        .select(`
          admission_number,
          user:users!students_user_id_fkey(full_name),
          class:classes!students_class_id_fkey(name, grade_level, section)
        `)
        .eq('parent_id', user.id)
        .maybeSingle();

      if (error || !data) return;

      const userData = data.user as { full_name: string } | null;
      const classData = data.class as { name: string; grade_level: string; section: string } | null;

      setChildInfo({
        full_name: userData?.full_name ?? 'Unknown',
        admission_number: data.admission_number ?? '',
        class_name: classData?.name ?? '',
        grade_level: classData?.grade_level ?? '',
        section: classData?.section ?? '',
      });
    } catch {
      // silently fail
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const filteredMenuItems = menuItems.filter(item =>
    user && item.roles.includes(user.role)
  );

  // Close notification panel when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setNotificationOpen(false);
      }
    }

    if (notificationOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [notificationOpen]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-red-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f6f7f9]">
      {/* Mobile backdrop overlay */}
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`print:hidden fixed left-0 top-0 h-full bg-white border-r border-gray-200/80 shadow-sm transition-all duration-300 z-40 flex flex-col ${sidebarOpen ? 'w-64' : isMobile ? 'w-0 overflow-hidden' : 'w-20'}`}>
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          {sidebarOpen && (
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-brand-50 ring-1 ring-brand-100 flex items-center justify-center flex-shrink-0">
                <img
                  src="/lamb.png"
                  alt="The Lamb College"
                  className="h-8 w-auto object-contain"
                />
              </div>
              <div>
                <h2 className="font-extrabold text-gray-900 text-sm tracking-tight">Lamb College</h2>
                <p className="text-[11px] text-brand-600 font-medium uppercase tracking-wider">School Portal</p>
              </div>
            </div>
          )}
          {!sidebarOpen && !isMobile && (
            <div className="h-10 w-10 rounded-xl bg-brand-50 ring-1 ring-brand-100 flex items-center justify-center mx-auto">
              <img
                src="/lamb.png"
                alt="The Lamb College"
                className="h-7 w-auto object-contain"
              />
            </div>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
          >
            <i className={`ri-${sidebarOpen ? 'menu-fold' : 'menu-unfold'}-line text-lg text-gray-500`}></i>
          </button>
        </div>

        <nav className="px-3 py-4 space-y-1 flex-1 overflow-y-auto app-scroll">
          {filteredMenuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => { if (isMobile) setSidebarOpen(false); }}
                title={!sidebarOpen ? item.label : undefined}
                className={`group flex items-center gap-3 rounded-xl transition-all duration-200 ${sidebarOpen ? 'px-3 py-2.5' : 'px-2.5 py-2.5 justify-center'} ${
                  isActive
                    ? 'bg-brand-gradient text-white shadow-brand'
                    : 'text-gray-600 hover:bg-brand-50 hover:text-brand-700'
                }`}
              >
                <span className={`flex items-center justify-center h-8 w-8 rounded-lg flex-shrink-0 transition-colors ${
                  isActive ? 'bg-white/20' : 'bg-gray-100 group-hover:bg-white'
                }`}>
                  <i className={`${item.icon} text-lg`}></i>
                </span>
                {sidebarOpen && <span className="text-sm font-semibold whitespace-nowrap">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* Child Info Card — parents only */}
        {user?.role === 'parent' && childInfo && (
          <div className={`border-t border-gray-100 ${sidebarOpen ? 'p-4' : 'p-2'}`}>
            {sidebarOpen ? (
              <div className="bg-brand-50 ring-1 ring-brand-100 rounded-xl p-3">
                <p className="text-[11px] font-semibold text-brand-600 uppercase tracking-wide mb-2 flex items-center gap-1">
                  <i className="ri-user-heart-line text-sm"></i>
                  Viewing Child
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-brand-gradient rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                    <span className="text-sm font-bold text-white">
                      {childInfo.full_name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{childInfo.full_name}</p>
                    <p className="text-xs text-gray-500 truncate">
                      {childInfo.class_name
                        ? `${childInfo.class_name}${childInfo.section ? ` · ${childInfo.section}` : ''}`
                        : childInfo.grade_level || 'No class assigned'}
                    </p>
                    {childInfo.admission_number && (
                      <p className="text-xs text-gray-400 truncate">#{childInfo.admission_number}</p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex justify-center">
                <div
                  className="w-9 h-9 bg-brand-gradient rounded-full flex items-center justify-center cursor-default shadow-sm"
                  title={childInfo.full_name}
                >
                  <span className="text-sm font-bold text-white">
                    {childInfo.full_name.charAt(0).toUpperCase()}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </aside>

      {/* Main Content — no margin shift on mobile */}
      <div className={`print:ml-0 transition-all duration-300 ${isMobile ? 'ml-0' : sidebarOpen ? 'ml-64' : 'ml-20'}`}>
        {/* Top Bar */}
        <header className="print:hidden bg-white/80 backdrop-blur-md border-b border-gray-200/80 sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 sm:px-6 py-3.5">
            <div className="flex items-center gap-3">
              {/* Hamburger for mobile */}
              {isMobile && (
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="w-9 h-9 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <i className="ri-menu-line text-xl text-gray-600"></i>
                </button>
              )}
              <div>
                <h1 className="text-lg sm:text-xl font-extrabold text-gray-900 tracking-tight">
                  Welcome, {getFirstName(user?.full_name)}
                </h1>
                <p className="text-xs sm:text-sm text-gray-500 capitalize flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-500"></span>
                  {user?.role} Portal
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <div className="relative" ref={notificationRef}>
                <button
                  onClick={() => setNotificationOpen(!notificationOpen)}
                  className="w-10 h-10 flex items-center justify-center text-gray-500 hover:text-brand-600 hover:bg-brand-50 rounded-xl transition-colors relative"
                >
                  <i className="ri-notification-3-line text-xl"></i>
                  {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 min-w-[18px] h-[18px] bg-brand-600 ring-2 ring-white text-white text-xs font-semibold rounded-full flex items-center justify-center px-1">
                      {unreadCount <= 9 ? unreadCount : '9+'}
                    </span>
                  )}
                </button>

                {notificationOpen && (
                  <NotificationPanel
                    onClose={() => setNotificationOpen(false)}
                    onUnreadCountChange={setUnreadCount}
                  />
                )}
              </div>

              <div className="flex items-center gap-2 sm:gap-3 sm:pl-3 sm:border-l sm:border-gray-200">
                <div className="w-10 h-10 bg-brand-gradient rounded-full flex items-center justify-center shadow-brand ring-2 ring-white">
                  <span className="text-sm font-bold text-white">
                    {getFirstName(user?.full_name).charAt(0).toUpperCase()}
                  </span>
                </div>
                <button
                  onClick={handleSignOut}
                  className="flex items-center gap-1.5 px-3 sm:px-4 py-2 text-sm font-semibold text-gray-600 hover:text-brand-700 hover:bg-brand-50 rounded-xl transition-colors whitespace-nowrap"
                >
                  <i className="ri-logout-box-r-line text-base"></i>
                  <span className="hidden sm:inline">Sign Out</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 sm:p-6 animate-fade-in-up">
          {children ?? <Outlet />}
        </main>
      </div>
    </div>
  );
}